// client/src/components/Clients/ClientForm.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Alert,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../../api/axiosConfig';

const createClient = async (data) => axios.post('/clients', data);
const updateClient = async ({ id, data }) => axios.put(`/clients/${id}`, data);

const ClientForm = ({ open, onClose, client, onSuccess }) => {
  const [form, setForm] = useState({
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    taxId: '',
    isActive: true,
  });
  const [error, setError] = useState('');
  const queryClient = useQueryClient();

  // Заполнение формы при открытии
  useEffect(() => {
    if (open) {
      // Сбрасываем ошибку при открытии формы
      setError('');
      if (client) {
        setForm({
          name: client.name || '',
          contactPerson: client.contactPerson || '',
          email: client.email || '',
          phone: client.phone || '',
          address: client.address || '',
          taxId: client.taxId || '',
          isActive: client.isActive !== undefined ? client.isActive : true,
        });
      } else {
        setForm({
          name: '',
          contactPerson: '',
          email: '',
          phone: '',
          address: '',
          taxId: '',
          isActive: true,
        });
      }
    }
  }, [client, open]);

  const mutation = useMutation({
    mutationFn: client ? updateClient : createClient,
    onSuccess: () => {
      queryClient.invalidateQueries(['clients']);
      setError(''); // очищаем ошибку при успехе
      onSuccess();
      onClose();
    },
    onError: (err) => {
      setError(err.response?.data?.message || 'Error saving client');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name) {
      setError('Client name is required');
      return;
    }
    mutation.mutate(client ? { id: client._id, data: form } : form);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{client ? 'Edit Client' : 'Add Client'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Client Name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contact Person"
                value={form.contactPerson}
                onChange={(e) => setForm({ ...form, contactPerson: e.target.value })}
                helperText="Optional"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                helperText="Optional"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                helperText="Optional"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={2}
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                helperText="Optional"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tax ID"
                value={form.taxId}
                onChange={(e) => setForm({ ...form, taxId: e.target.value })}
                helperText="Optional"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    color="primary"
                  />
                }
                label={form.isActive ? 'Active' : 'Inactive'}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ClientForm;